/* eslint-disable */
import React, {
  useEffect,
  useState,
  useMemo,
  useContext,
  useCallback,
} from "react";
import Loading from "./loader/Loading"; // Import Loader component
import { toast } from "react-toastify";
import { MaterialReactTable } from "material-react-table";
import { FaPlus } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "@/context/ContextFile";
import Swal from "sweetalert2";
import { errorPopup, successPopup, warningPopup } from "@/helpers";
import { useListing, usePayment } from "@/providers";
import { axiosInstanceWithToken } from "@/helpers/axios";
import { useProfile } from "@/hooks";
import { PROFILE_IMAGE_SERVER_URL, SERVER_IMAGES_URL } from "@/constants";
import { FaCreditCard } from "react-icons/fa";
import { useElements, useStripe } from "@stripe/react-stripe-js";

const List = () => {
  const [loading, setLoading] = useState(true);
  const [pageCome, setPageCome] = useState(true);
  const path = useNavigate();
  const [activeButton, setActiveButton] = useState("All");
  const { profileCompleteness } = useProfile();
  const { paymentMethod } = usePayment();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { getListingsByUser } = useListing();
  const [page, setPage] = useState(1);
  const [listings, setListings] = useState([]);
  const [selectedListing, setSelectedListing] = useState();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const stripe = useStripe();
  const elements = useElements();

  const loadListings = useCallback(async () => {
    if (!hasMore) return;
    setLoading(true);
    try {
      let params = {
        page,
        per_page: 100,
      };

      switch (activeButton) {
        case "All":
          break;
        case "Published":
          params.is_enabled = 1;
          params.status = 1;
          params.is_published = 1;
          break;
        case "Featured":
          params.is_enabled = 0;
          params.status = 1;
          params.is_draft = 0;
          params.is_featured = 1;
          break;
        case "Drafts":
          params.is_enabled = 0;
          params.status = 0;
          params.is_draft = 1;
          break;
        case "Waiting Approval":
          params.is_enabled = 0;
          params.status = 0;
          params.is_draft = 0;
          params.is_published = 0;
          break;
        case "Deactivated":
          params.is_enabled = 0;
          params.status = 1;
          params.is_draft = 0;
          params.is_featured = 0;
          break;
      }

      const res = await getListingsByUser(params);
      const isNotEmpty =
        res?.data?.listings?.length > 0 || res?.data?.drafts?.length > 0;
      if (isNotEmpty) {
        setListings((prevListings) => {
          const newListings = [
            ...prevListings,
            ...(res?.data?.listings || res?.data?.drafts),
          ];
          return Array.from(
            new Map(newListings.map((item) => [item?.id, item])).values()
          );
        });
        setPage(res?.data?.current_page);
      } else {
        setHasMore(res?.data?.current_page === res?.data?.last_page);
      }
    } catch (error) {
      console.error("Error fetching listings:", error);
    } finally {
      setLoading(false);
    }
  }, [page, activeButton, hasMore]);

  useEffect(() => {
    setListings([]);
    setPage(1);
    setHasMore(true);
    loadListings();
  }, [activeButton]);

  useEffect(() => {
    if (pageCome) {
      Swal.fire({
        icon: "info",
        text: "Please scroll right and left to view more columns.",
        showConfirmButton: true,
      }).then(() => {
        setPageCome(false);
      });
    }
  }, []);

  const handleDelete = async (id) => {
    try {
      const response = await axiosInstanceWithToken.delete(`/listing/${id}`);

      if (response?.status) {
        successPopup("", response?.message);
      }

      setListings((prevListings) =>
        prevListings.filter((listing) => listing.id !== id)
      );
    } catch (error) {
      toast.error();
      errorPopup("", "Error deleting item");
    }
  };

  const toggleEnable = async (id, is_disable) => {
    try {
      const formData = new FormData();
      formData.append("is_enabled", is_disable === 1 ? 1 : 0);
      formData.append("_method", "PATCH");
      const response = await axiosInstanceWithToken.post(
        `/listing/${id}`,
        formData
      );

      if (response?.data?.status) {
        successPopup("", response?.data.message);

        const updatingListing = response?.data?.listing;

        // Update the is_disable property of the specific listing
        setListings((prevListings) =>
          prevListings.map((listing) =>
            listing.id === id
              ? { ...listing, is_disable: is_disable === 1 ? 0 : 1 }
              : listing
          )
        );
      }
    } catch (error) {
      toast.error();
      errorPopup("", "Error deleting item");
    }
  };

  const handleUpgrade = async () => {
    try {
      if (!stripe || !elements) {
        await Swal.fire({
          icon: "error",
          title: "Error",
          text: "Payment system not initialized. Please try again later.",
        });
        return;
      }

      const payment_method = selectedPaymentMethod;
      const formData = new FormData();
      formData.append("payment_method", payment_method);

      const response = await axiosInstanceWithToken.post(
        `/listing/upgrade/${selectedListing}`,
        formData
      );

      if (response?.data?.status) {
        await Swal.fire({
          icon: "success",
          title: "Success",
          text: response?.data?.message,
          timer: 3000,
          showConfirmButton: false,
        });

        const clientSecret = response?.data?.payment_intent?.client_secret;
        const returnUrl = `${window.location.origin}/listing-status`;

        const { error: stripeError, paymentIntent } =
          await stripe.confirmPayment({
            clientSecret,
            payment_method: selectedPaymentMethod,
            confirmParams: {
              return_url: returnUrl,
            },
          });

        if (stripeError) {
          await Swal.fire({
            icon: "error",
            title: "Payment Failed",
            text:
              stripeError.message ||
              "There was an issue processing your payment.",
          });
          return;
        }
      }

      setListings((prevListings) =>
        prevListings.filter((listing) => listing.id !== selectedListing)
      );
    } catch (error) {
      console.error("Upgrade error:", error);
      let errorMessage = "Error Upgrading Listing!";

      if (error.response) {
        // Handle HTTP errors
        errorMessage = error.response.data.message || errorMessage;
      } else if (error.request) {
        // Handle network errors
        errorMessage = "Network error. Please check your connection.";
      }

      await Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
      });
    }
  };
  console.log({ activeButton });
  const columns = useMemo(
    () => [
      {
        accessorKey: "id",
        header: "ID",
        size: 100,
      },
      {
        accessorKey: "images",
        header: "Image",
        size: 100,
        Cell: ({ row }) => (
          <img
            src={
              row?.original?.host?.image !== null
                ? `${PROFILE_IMAGE_SERVER_URL}/${row?.original?.host?.image}`
                : "images/avatar/avatar-bg.png"
            }
            // src={row?.original?.images[0]?.image !== null ? `${SERVER_IMAGES_URL}/listing/${row?.original?.images[0]?.image}` : "images/avatar/avatar-bg.png"}
            alt="Host"
            style={{ width: "50px", height: "50px", borderRadius: "50%" }}
          />
        ),
      },

      {
        accessorKey: "title",
        header: "Title",
        size: 100,
      },
      {
        accessorKey: "listing_type",
        header: "Listing Type",
        size: 150,
      },
      {
        accessorKey: "lodging_type",
        header: "Lodging Type",
        size: 150,
      },
      {
        accessorKey: "type_of_space",
        header: "Type of Space",
        size: 150,
      },
      {
        accessorKey: "nightly_price",
        header: "Price",
        size: 100,
        Cell: ({ row }) => {
          return `$${row.original.nightly_price}`;
        },
      },
      {
        accessorKey: "no_of_bedrooms",
        header: "Bedrooms",
        size: 100,
      },
      {
        accessorKey: "no_of_bathrooms",
        header: "Bathrooms",
        size: 100,
      },
      {
        accessorKey: "no_of_guest",
        header: "Guests",
        size: 100,
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 150,
        Cell: ({ row }) => {
          const status = row.getValue("status");
          const is_draft = row.original.is_draft;
          const is_featured = row.original.is_featured;
          const is_enabled = row.original.is_enabled;
          const is_disable = row.original.is_disable; // Add this line

          const getButtonClass = () => {
            if (+is_disable === 1) return "bg-red-500 text-white"; // Deactivated (Red)
            if (+is_enabled === 0 && +status === 1)
              return "bg-red-500 text-white"; // Deactivated (Red)
            if (+is_draft === 1 || activeButton === "Drafts")
              return "bg-gray-500 text-white"; // Draft (Gray)
            if (+is_featured === 1) return "bg-yellow-400 text-black"; // Featured (Gold)
            if (+status === 1) return "bg-[#8ec639] text-white"; // Approved (Green)
            return "bg-yellow-300 text-black"; // Waiting for Approval (Yellow)
          };

          const getStatus = () => {
            if (+is_disable === 1) return "DEACTIVATED"; // Check is_disable first
            if (+is_enabled === 0 && +status === 1) return "DEACTIVATED";
            if (+is_draft === 1 || activeButton === "Drafts") return "DRAFT";
            if (+is_featured === 1) return "FEATURED";
            if (+status === 1) return "APPROVED";
            return "WAITING FOR APPROVAL";
          };

          return (
            <div>
              <button
                className={`truncate ${getButtonClass()}`}
                style={{
                  padding: "7px 10px",
                  borderRadius: "5px",
                }}
              >
                {getStatus()}
              </button>
            </div>
          );
        },
      },

      {
        accessorKey: "action",
        header: "Actions",
        size: 200,
        Cell: ({ row }) => {
          const is_featured = row.original.is_featured;
          const status = row.original.status;
          const is_disable = row.original.is_disable;
          const is_draft = row.original.is_draft;

          console.log(is_draft);
          // const is_published = row.original.is_published;
          return (
            <select
              className="shadow-md bg-white cursor-pointer p-[10px] rounded border-soft"
              onChange={(e) => {
                const selectedAction = e.target.value;

                switch (selectedAction) {
                  case "view":
                    window.open(`/listing/${row.original.id}`, "_blank");
                    break;

                  case "edit":
                    path(
                      `/edit_listing/${row.original.id}${
                        activeButton === "Drafts" ? "?isDraft=true" : ""
                      }`,
                      {
                        state: {
                          listId: row.original.id,
                          isDraft: +row.original.is_draft === 1,
                        },
                      }
                    );
                    break;

                  case "duplicate":
                    path(`/edit_listing/${row.original.id}?duplicate=true`, {
                      state: { listId: row.original.id, isDuplicate: true },
                    });
                    break;

                  case "featured":
                    // Add logic for updating to featured
                    setSelectedListing(row.original?.id);
                    setIsModalOpen(true);
                    // console.log("Update to featured");
                    break;

                  case "deactivate":
                    // Add logic for deactivation
                    toggleEnable(row.original?.id, row.original?.is_disable);
                    // console.log("Deactivate");
                    break;

                  case "delete":
                    handleDelete(row.original.id);
                    break;

                  default:
                    break;
                }
              }}
            >
              <option value="">Select Action</option>
              {is_draft?.toString() === "1" ? (
                <> </>
              ) : (
                <option value="view">View</option>
              )}
              <option value="edit">Edit</option>
              {is_draft?.toString() === "1" ? (
                <> </>
              ) : (
                <option value="duplicate">Duplicate</option>
              )}
              {is_featured === 0 && is_disable === 0 && status !== 0 && (
                <option value="featured">Upgrade to Featured</option>
              )}

              {row?.original?.status === 1 &&
                row?.original?.is_disable === 0 && (
                  <option value="deactivate">Deactivate</option>
                )}

              {row?.original?.status === 1 &&
                row?.original?.is_disable === 1 && (
                  <option value="activate">Activate</option>
                )}

              <option value="delete">Delete</option>
            </select>
          );
        },
      },
    ],
    [path, activeButton]
  );

  const [show, setShow] = useState(false);
  const { listingValue, setListingValue } = useContext(ThemeContext);

  const handleOpenModal = () => {
    if (profileCompleteness === 100) {
      setShow(true);
    } else {
      setTimeout(() => {
        warningPopup(`
          <div>
            <p style="text-align: center">Please complete your profile to 100%. You will not be able to host or book until it is complete.</p>
            <p style="text-align: center">
             Once we have verified your identity, you will receive a message letting you know you are all set.
            </p>
            <p style="text-align: center">
             To complete your profile properly, watch the Registration video on our <br/> <a  target="_blank" style="color: #af2322; text-decoration: underline;" href="https://youtube.com/@fly-inn">YouTube Channel</a>.
            </p>
          </div>
          `);
      }, 1000);
    }
  };

  const handleClose = () => {
    if (listingValue) {
      localStorage.setItem("listingValue", listingValue);
      setShow(false);
      path("/add_listing", { state: { listing_type: listingValue } });
      // path("/add_listing");
    }
  };

  const buttonLabels = [
    "All",
    "Published",
    "Featured",
    "Drafts",
    "Waiting Approval",
    "Deactivated",
  ];

  return (
    <>
      {show && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black bg-opacity-50">
          <div className="z-10 w-full max-w-md bg-white rounded-lg shadow-lg">
            {/* Close button */}
            <div className="flex justify-between px-6 py-4 text-xl font-semibold border-b border-gray-200">
              <span>Listing Type</span>
              <button
                onClick={() => setShow(false)}
                className="z-[50] w-6 h-6 group hover:text-gray-500 rounded-full shadow-md hover:bg-gray-100 bg-gray-300 flex items-center justify-center relative right-0 top-0"
              >
                <i className="text-white fas fa-times group-hover:text-gray-500"></i>
              </button>
            </div>

            <div className="px-6 py-4">
              <div className="w-full">
                <div className="mb-4">
                  <select
                    defaultValue={listingValue}
                    onChange={(e) => setListingValue(e.target.value)}
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-500"
                  >
                    <option disabled value="">
                      All categories
                    </option>
                    <option value="short-term rental">Short-Term Rental</option>
                    <option disabled value={"Adventures"}>
                      Adventure (Coming Soon)
                    </option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex justify-end w-full px-6 py-4 border-t border-gray-200">
              <button
                onClick={handleClose}
                className="bg-[#8ec639] text-white px-4 py-2 rounded-md hover:bg-[#7db132]"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <Loading />
      ) : (
        <div className="px-4">
          <div style={{ textAlign: "center" }} className="dashboard-title">
            <h3 style={{ color: "#3b4249" }}>My Listings</h3>
          </div>
          <div className="flex my-5">
            <button
              onClick={handleOpenModal}
              className="flex justify-center items-center bg-[#54c4d9] text-md font-medium cursor-pointer text-white py-3 px-8 rounded-md"
            >
              <FaPlus />
              New Listing
            </button>
          </div>
          <div className="flex flex-col space-y-4">
            <div className="flex flex-wrap gap-4 mb-4">
              <button
                className={`listing_btn ${
                  activeButton === "All" ? "active" : ""
                } w-full sm:w-[120px] text-center truncate`}
                onClick={() => setActiveButton("All")}
              >
                All
              </button>
              <button
                className={`listing_btn ${
                  activeButton === "Published" ? "active" : ""
                } w-full sm:w-[120px] text-center truncate`}
                onClick={() => setActiveButton("Published")}
              >
                Published
              </button>
              <button
                className={`listing_btn ${
                  activeButton === "Featured" ? "active" : ""
                } w-full sm:w-[120px] text-center truncate`}
                onClick={() => setActiveButton("Featured")}
              >
                Featured
              </button>
              <button
                className={`listing_btn ${
                  activeButton === "Drafts" ? "active" : ""
                } w-full sm:w-[120px] text-center truncate`}
                onClick={() => setActiveButton("Drafts")}
              >
                Drafts
              </button>
              <button
                className={`listing_btn ${
                  activeButton === "Waiting Approval" ? "active" : ""
                } w-full sm:w-[120px] text-center truncate`}
                onClick={() => setActiveButton("Waiting Approval")}
              >
                Waiting Approval
              </button>
              <button
                className={`listing_btn ${
                  activeButton === "Deactivated" ? "active" : ""
                } w-full sm:w-[120px] text-center truncate`}
                onClick={() => setActiveButton("Deactivated")}
              >
                Deactivated
              </button>
            </div>
          </div>
          <div className="max-w-full overflow-x-auto">
            <MaterialReactTable
              columns={columns}
              data={listings}
              // rowCount={100}
              muiPaginationProps={{
                rowsPerPageOptions: [
                  { value: 10, label: "10" },
                  { value: 25, label: "25" },
                  { value: 50, label: "50" },
                  { value: 100, label: "100" },
                  { value: listings?.length, label: "All" },
                ],
                showFirstButton: false,
                showLastButton: false,
              }}
            />
          </div>

          {isModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-800 bg-opacity-75">
              <div className="relative w-full max-w-lg p-6 bg-white rounded-lg shadow-lg">
                <div className="flex justify-end ">
                  <i
                    className="px-2 py-1 text-white bg-gray-300 rounded-full cursor-pointer fas fa-times"
                    onClick={() => setIsModalOpen(false)}
                  ></i>
                </div>
                <>
                  <h2 className="mb-4 text-2xl font-bold border-b">
                    How Would you like to Pay!
                  </h2>
                  {/* <h4 className="p-4 mb-4 text-lg font-bold text-center bg-yellow-300 border-b rounded-md shadow-lg">Was $99, Now is $49</h4> */}

                  <div className="space-y-2 h-[300px] overflow-scroll">
                    {paymentMethod?.length > 0 ? (
                      <ul>
                        {paymentMethod.map(
                          (method) =>
                            method.type === "card" && (
                              <li
                                key={method.id}
                                className={`flex justify-between items-center mb-2 p-3 border border-b ${
                                  selectedPaymentMethod === method?.id &&
                                  "bg-gray-200"
                                }`}
                              >
                                <div className={`flex items-center `}>
                                  <input
                                    type="radio"
                                    name="selectedPaymentMethod"
                                    id={`payment-method-${method.id}`}
                                    value={method.id}
                                    onChange={(e) =>
                                      setSelectedPaymentMethod(e.target.value)
                                    }
                                    className="!mr-4"
                                  />
                                  <label
                                    htmlFor={`payment-method-${method.id}`}
                                    className="flex items-center"
                                  >
                                    <FaCreditCard className="inline mr-2" />
                                    **** **** **** {method?.card?.last4} -{" "}
                                    {method?.billing_details?.name}
                                  </label>
                                </div>
                              </li>
                            )
                        )}
                      </ul>
                    ) : (
                      <h3 className="mb-2 text-xl font-semibold text-left">
                        No Payment Method Found!
                      </h3>
                    )}
                  </div>
                  <div className="flex justify-end">
                    <button
                      onClick={() => handleUpgrade()}
                      className="bg-[#8ec639] hover:bg-[#7fb134] px-4 py-2 mt-4 text-white rounded-md text-md font-bold"
                    >
                      Pay
                      <span className="mx-2 text-white line-through">
                        ($99)
                      </span>
                      <span className="mr-2 text-white">/ $52.56</span>
                    </button>
                  </div>
                </>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default List;
